import { Alert, Button, Spin } from 'antd';
import { useGetSharedLifeStoryQuery } from 'api/InboxApi';
import { useGetPublicLifeStoryQuery } from 'api/LifeReviewApi';
import FamilyViewRenderer from 'components/LifeStory/FamilyViewRenderer';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { selectAuthToken } from 'store/authSlice';
import { getLifeStoryResponseText } from 'utils/lifeStory';
import {
  getCanonicalLifeStoryPathFromHash,
  getCanonicalLifeStoryPathFromQuery,
  getTokenFromHash,
  getTokenFromSearch
} from 'utils/publicRoutePrivacy';
import { getCurrentUrl } from 'utils/utils';
import './LifeStorySharePage.css';

const LIFE_STORY_POLLING_INTERVAL = 30000;


type NormalizedResponse = {
  id?: string;
  promptText?: string;
  text?: string;
  audioUrl?: string;
  sectionTitle?: string;
  sectionIndex?: number;
  responseIndex?: number;
  recordedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

type NormalizedSection = {
  id?: string;
  key?: string;
  title?: string;
  description?: string;
  responses: NormalizedResponse[];
};

const getLifeStoryFullName = (
  lifeStory?: { firstName?: string; lastName?: string; fullName?: string },
  defaultText = 'Your loved one'
) => {
  return lifeStory?.fullName?.trim() || [lifeStory?.firstName, lifeStory?.lastName].filter(Boolean).join(' ').trim() || defaultText;
};

export function LifeStorySharePage() {
  const navigate = useNavigate();
  const { token: tokenFromParams, inviteId } = useParams();
  const { pathname, search, hash } = useLocation();
  const authToken = useSelector(selectAuthToken);
  const isAuthenticated = Boolean(authToken);
  const [audioErrorVersion, setAudioErrorVersion] = useState(0);

  const hashToken = getTokenFromHash(hash);
  const queryToken = getTokenFromSearch(search);
  const token = tokenFromParams || hashToken || queryToken;

  useEffect(() => {
    const canonicalPathFromHash = getCanonicalLifeStoryPathFromHash(hash);
    if (!tokenFromParams && canonicalPathFromHash) {
      navigate(canonicalPathFromHash, { replace: true });
      return;
    }

    const canonicalPathFromQuery = getCanonicalLifeStoryPathFromQuery(pathname, search);
    if (!tokenFromParams && !hashToken && canonicalPathFromQuery) {
      navigate(canonicalPathFromQuery, { replace: true });
    }
  }, [hash, hashToken, navigate, pathname, search, tokenFromParams]);

  useEffect(() => {
    const previousRobotsMeta = document.querySelector('meta[name="robots"]')?.getAttribute('content') || null;
    const previousReferrerMeta = document.querySelector('meta[name="referrer"]')?.getAttribute('content') || null;

    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex,nofollow');

    let referrerMeta = document.querySelector('meta[name="referrer"]');
    if (!referrerMeta) {
      referrerMeta = document.createElement('meta');
      referrerMeta.setAttribute('name', 'referrer');
      document.head.appendChild(referrerMeta);
    }
    referrerMeta.setAttribute('content', 'no-referrer');

    return () => {
      if (previousRobotsMeta === null) {
        robotsMeta?.remove();
      } else {
        robotsMeta?.setAttribute('content', previousRobotsMeta);
      }

      if (previousReferrerMeta === null) {
        referrerMeta?.remove();
      } else {
        referrerMeta?.setAttribute('content', previousReferrerMeta);
      }
    };
  }, []);

  const {
    data: publicLifeStory,
    isLoading: isPublicLifeStoryLoading,
    isFetching: isPublicLifeStoryFetching,
    error: publicLifeStoryError,
    refetch: refetchPublicLifeStory
  } = useGetPublicLifeStoryQuery(
    { token },
    {
      skip: !token,
      pollingInterval: LIFE_STORY_POLLING_INTERVAL,
      refetchOnFocus: true,
      refetchOnReconnect: true
    }
  );

  const {
    data: privateLifeStory,
    isLoading: isPrivateLifeStoryLoading,
    isFetching: isPrivateLifeStoryFetching,
    error: privateLifeStoryError,
    refetch: refetchPrivateLifeStory
  } = useGetSharedLifeStoryQuery(
    { inviteId: inviteId || '' },
    {
      skip: !!token || !isAuthenticated || !inviteId,
      pollingInterval: LIFE_STORY_POLLING_INTERVAL,
      refetchOnFocus: true,
      refetchOnReconnect: true
    }
  );

  const shouldRenderPrivateView = !token && isAuthenticated && !!inviteId;
  const lifeStory = shouldRenderPrivateView ? privateLifeStory : publicLifeStory;
  const isLoading = shouldRenderPrivateView ? isPrivateLifeStoryLoading : isPublicLifeStoryLoading;
  const isFetching = shouldRenderPrivateView ? isPrivateLifeStoryFetching : isPublicLifeStoryFetching;
  const error = shouldRenderPrivateView ? privateLifeStoryError : publicLifeStoryError;
  const refetch = shouldRenderPrivateView ? refetchPrivateLifeStory : refetchPublicLifeStory;

  useEffect(() => {
    if (shouldRenderPrivateView || !isAuthenticated || !token || !inviteId) {
      return;
    }

    navigate(`/app/shared-with-me/life-stories/${inviteId}`, { replace: true });
  }, [shouldRenderPrivateView, isAuthenticated, token, inviteId, navigate]);

  useEffect(() => {
    if (audioErrorVersion > 0) {
      refetch();
    }
  }, [audioErrorVersion, refetch]);

  const normalizedSections = useMemo<NormalizedSection[]>(() => {
    const sourceSections = lifeStory?.sections || [];

    return sourceSections
      .map((section, sectionIndex) => {
        const sectionResponses = section?.responses || [];

        const promptResponses = (section?.prompts || []).flatMap((prompt) => {
          const responseList = prompt?.responses || [];
          const singleResponse = prompt?.response ? [prompt.response] : [];
          const promptText = prompt?.prompt || prompt?.promptText || prompt?.question;

          return [...responseList, ...singleResponse].map((response) => ({
            promptText,
            response
          }));
        });
        const allResponses: Array<{ response?: (typeof sectionResponses)[number]; promptText?: string }> = [
          ...sectionResponses.map((response) => ({ response })),
          ...promptResponses
        ];

        const normalizedResponses = allResponses
          .map((item, responseIndex) => {
            const response = item.response;
            const text = getLifeStoryResponseText(response);
            const audioUrl = response?.audioUrl || response?.audioDownloadUrl;
            if (!text && !audioUrl) {
              return null;
            }

            return {
              id: response?.id,
              promptText: item.promptText,
              text,
              audioUrl,
              sectionTitle: section?.title,
              sectionIndex,
              responseIndex,
              recordedAt: response?.recordedAt,
              createdAt: response?.createdAt,
              updatedAt: response?.updatedAt
            };
          })
          .filter(Boolean) as NormalizedResponse[];

        if (!normalizedResponses.length) {
          return null;
        }

        return {
          id: section?.id,
          key: section?.key || section?.sectionKey,
          title: section?.title,
          description: section?.description,
          responses: normalizedResponses
        };
      })
      .filter(Boolean) as NormalizedSection[];
  }, [lifeStory]);

  const hasRenderableContent = normalizedSections.length > 0;
  const patientName = getLifeStoryFullName(lifeStory, 'Your loved one');
  const firstName =
    lifeStory?.firstName?.trim() ||
    lifeStory?.fullName?.trim()?.split(/\s+/)[0] ||
    patientName.split(/\s+/)[0] ||
    'your loved one';

  const errorStatus = Number((error as { status?: number | string } | undefined)?.status);
  const isInvalidToken = errorStatus === 400 || errorStatus === 404;
  const isInactiveInvite = errorStatus === 410;
  const isAuthError = errorStatus === 401 || errorStatus === 403;
  const isGenericError = Boolean(error) && !isInvalidToken && !isInactiveInvite;

  if (isLoading) {
    return (
      <PublicPageShell>
        <div className="life-story-state life-story-state-loading">
          <Spin size="large" />
        </div>
      </PublicPageShell>
    );
  }

  if ((!token && !shouldRenderPrivateView) || (!isLoading && (isInvalidToken || isInactiveInvite || isGenericError))) {
    const title = !token && !shouldRenderPrivateView
      ? 'Invalid link'
      : isInactiveInvite
        ? 'This Life Story invitation is no longer active.'
        : isInvalidToken
          ? 'Invalid link. Please check your Life Story invitation URL.'
          : 'Unable to load this shared page';
    const description = !token && !shouldRenderPrivateView
      ? 'Please check your Life Story invitation URL and try again.'
      : isInactiveInvite
        ? 'Please contact the sender if you need a new invitation.'
        : isInvalidToken
          ? 'The invitation link may be incorrect, expired, or already used.'
          : 'This page could not be loaded right now. Please check your connection and try again.';

    return (
      <PublicPageShell>
        <main className="life-story-state-page">
          <Alert type="error" showIcon message={title} description={description} />
          {token && !isAuthenticated && isAuthError && (
            <div className="life-story-auth-actions">
              <Button
                type="primary"
                onClick={() =>
                  navigate('/login', {
                    state: { redirectUrl: getCurrentUrl() }
                  })
                }
              >
                Sign in to continue
              </Button>
            </div>
          )}
          {token && (
            <Button className="life-story-refresh-button" onClick={() => refetch()}>
              Refresh page
            </Button>
          )}
        </main>
      </PublicPageShell>
    );
  }

  const isEmpty = !isFetching && !hasRenderableContent;

  return (
    <PublicPageShell>
      {isEmpty ? (
        <main className="life-story-state-page">
          <div className="life-story-empty-card">
            <h2>No Life Story content is available yet.</h2>
            <p>New responses may appear here once stories are added.</p>
          </div>
        </main>
      ) : (
        <main className="life-story-renderer-page">
          <FamilyViewRenderer
            token={token}
            lifeStory={lifeStory}
            patientName={patientName}
            firstName={firstName}
            showAccountPrompt={!isAuthenticated}
            tenantOrganizationName={lifeStory?.tenantOrganizationName}
            tenantOrganizationLogoUrl={lifeStory?.tenantOrganizationLogoUrl}
            sections={normalizedSections}
            onAudioError={() => setAudioErrorVersion((v) => v + 1)}
          />
        </main>
      )}
    </PublicPageShell>
  );
}

function PublicPageShell({ children }: { children: ReactNode }) {
  return <div className="life-story-public-shell">{children}</div>;
}
